import { NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { FlexLayoutModule } from "@angular/flex-layout";
import { AutoCompleteModule } from "@bfx/primeng/autocomplete";
import { ButtonModule } from "@bfx/primeng/button";
import { CalendarModule } from "@bfx/primeng/calendar";
import { CheckboxModule } from "@bfx/primeng/checkbox";
import { DataTableModule } from "@bfx/primeng/datatable";
import { DropdownModule } from "@bfx/primeng/dropdown";
import { InputMaskModule } from "@bfx/primeng/inputmask";
import { InputTextareaModule } from "@bfx/primeng/inputtextarea";
import { InputTextModule } from "@bfx/primeng/inputtext";
import { ObjectUtils } from "@bfx/primeng/utils";
import { PanelModule } from "@bfx/primeng/panel";
import { SelectButtonModule } from "@bfx/primeng/selectbutton";
import { SpinnerModule } from "@bfx/primeng/spinner";
import { TabViewModule } from "@bfx/primeng/tabview";
import { PerfectScrollbarModule } from "@bfx/ngx-perfect-scrollbar";
import { PerfectScrollbarConfigInterface } from "@bfx/ngx-perfect-scrollbar";
import { PERFECT_SCROLLBAR_CONFIG } from "@bfx/ngx-perfect-scrollbar";

// modules
import { BfxSharedModule } from "../bfx-shared/bfx-shared.module";
import { OEInterfaceControlsModule } from "./oe-interface-controls.module";
import { BfxElementsLibraryModule } from "../bfx-element-library/bfx-elements-library.module";
import { OEAlgoStrategyModule } from "./oe-algo-strategy.module";
import { ICControlsModule } from "../ic-controls/ic-controls.module";
// components
import { OEActionsPanelComponent } from "./interface/oe-panel-actions.component";
import { OENotificationsPanelComponent } from "./interface/oe-panel-notifications.component";
import { OEStrategyPanelComponent } from "./interface/oe-panel-strategy.component";
import { OEGammaViewerComponent } from "./interface/oe-panel-gamma-viewer.component";
import { OEScreenContainerComponent } from "./interface/oe-screen-container.component";
import { OESpotEntryComponent } from "./interface/oe-panel-spot-entry.component";
import { OEAlgoEntryComponent } from "./interface/oe-panel-algo-entry.component";
import { OENdfEntryComponent } from "./interface/oe-panel-ndf-entry.component";
import { OEBmarkEntryComponent } from "./interface/oe-panel-bmark-entry.component";
import { OEDebugPanelComponent } from "./interface/oe-panel-debug.component";
import { OEAlgoParamsComponent } from "./interface/oe-panel-algo-params.component";
import { OEGammaEntryComponent } from "./interface/oe-panel-gamma-entry.component";
import { OECustomisePanelComponent } from "./interface/oe-panel-customise.component";

// services
import { OERateSubscriptionManagerService } from "./services/basic/oe-rate-subscription-manager.service";
import { OEModelLockingService } from "./services/model/oe-model-locking.service";
import { OESyncService } from "./services/oe-sync.service";
import { OEValidatorService } from "./services/oe-validator.service";
import { OEModelMasterService } from "./services/model/oe-model-master.service";
import { OEModelGetterService } from "./services/model/oe-model-getter.service";
import { OEModelSetterService } from "./services/model/oe-model-setter.service";
import { OEModelSyncService } from "./services/model/oe-model-sync.service";
import { OEModelOperationsService } from "./services/model/oe-model-operations.service";
import { OECoordinatorService } from "./services/oe-coordinator.service";
import { OEApiService } from "./services/oe-api.service";
import { OEStrategyLayoutService } from "./services/oe-strategy-layout.service";
import { OEToleranceService } from "./services/basic/oe-tolerance.service";
import { OETimeMachineService } from "./services/basic/oe-time-machine.service";
import { OEUtilityService } from "./services/basic/oe-utility.service";
import { OELoaderService } from "./services/oe-loader.service";
import { OEPermissionsService } from "./services/basic/oe-permissions.service";
import { OESettingsService } from "./services/basic/oe-settings.service";
import { OEScreenRegistryService } from "./services/basic/oe-screen-registry.service";
import { OEWorkerActionsService } from "./services/worker/oe-worker-actions.service";
import { OEWorkerListenerService } from "./services/worker/oe-worker-listener.service";
import { OEActionsService } from "./services/oe-actions.service";
import { OEDialogService } from "./services/basic/oe-dialogs.service";
import { OECloserService } from "./services/oe-closer.service";
import { OEAccountMediatorService } from "./services/basic/oe-account-mediator.service";
import { OEStrategyRenderService } from "./services/basic/oe-strategy-render.service";
import { OELookupService } from "./services/basic/oe-lookup.service";
import { OELoggerService } from "./services/basic/oe-logger.service";
import { OENotificationsService } from "./services/basic/oe-notifications.service";
import { OEAdapterForwardService } from "./services/adapter/oe-adapter-forward.service";
import { OEAdapterReverseService } from "./services/adapter/oe-adapter-reverse.service";
import { OEDatasetService } from "./services/basic/oe-dataset.service";
import { NativeDesktopModule } from "../bfx-native-desktop/native-desktop-support.module";
import { OELogSettingsService } from "app/order-entry/services/basic/oe-log-settings.service";
import { OEAdapterGammaHelper } from "./services/adapter/oe-adapter-gamma-helper";
import { BfxDetailFillEditorsModule } from "../bfx-detail-fill/bfx-detail-fill-editors.module";
import { OEBmarkAllocationGridComponent } from "./interface/oe-panel-bmark-allocation-grid.component";
import { AgGridModule } from "@bfx/ag-grid-angular";
import { OEAllocationBulkService } from "./services/oe-allocation-bulk.service";
import { BaseComponentsModule } from "../../shared/base-components/base-components.module";
import { BfxPipeModule } from "../bfx-shared/bfx-pipes.module";
import { BfxExecutionMonitorModule } from "app/bfx-execution-monitor/execution-monitor.module";
import { OEAPIChannelService } from "app/order-entry/services/oe-api-channel.service";
import { OEUpdateMonitorService } from "app/order-entry/services/oe-update-monitor.service";
import { OENdfAlgoEntryComponent } from "./interface/oe-panel-ndf-algo-entry.component";

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
};

@NgModule({
  imports: [
    CommonModule,
    TranslateModule,
    FlexLayoutModule,
    FormsModule,
    DataTableModule,
    InputTextModule,
    CheckboxModule,
    PanelModule,
    SpinnerModule,
    ButtonModule,
    AutoCompleteModule,
    DropdownModule,
    InputMaskModule,
    BfxElementsLibraryModule,
    SelectButtonModule,
    CalendarModule,
    TabViewModule,
    InputTextareaModule,
    NativeDesktopModule,
    PerfectScrollbarModule,
    BfxSharedModule,
    OEInterfaceControlsModule,
    OEAlgoStrategyModule,
    BaseComponentsModule,
    ICControlsModule,
    BfxDetailFillEditorsModule,
    BfxPipeModule,
    AgGridModule,
    BfxExecutionMonitorModule,
  ],
  declarations: [
    OEScreenContainerComponent,
    OESpotEntryComponent,
    OEAlgoEntryComponent,
    OENdfAlgoEntryComponent,
    OENdfEntryComponent,
    OEBmarkEntryComponent,
    OEActionsPanelComponent,
    OENotificationsPanelComponent,
    OEStrategyPanelComponent,
    OEGammaViewerComponent,
    OEDebugPanelComponent,
    OEAlgoParamsComponent,
    OEGammaEntryComponent,
    OECustomisePanelComponent,
    OEBmarkAllocationGridComponent,
  ],
  providers: [
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },
    // model
    OEModelMasterService,
    OEModelGetterService,
    OEModelSetterService,
    OEModelOperationsService,
    OEModelLockingService,
    OEModelSyncService,
    // basic
    OEAccountMediatorService,
    OECloserService,
    OEDatasetService,
    OEDialogService,
    OELoggerService,
    OELookupService,
    OENotificationsService,
    OEPermissionsService,
    OERateSubscriptionManagerService,
    OESettingsService,
    OELogSettingsService,
    OEScreenRegistryService,
    OEStrategyRenderService,
    OETimeMachineService,
    OEToleranceService,
    OEUtilityService,
    // adapter
    OEAdapterForwardService,
    OEAdapterReverseService,
    OEAdapterGammaHelper,
    //worker
    OEWorkerActionsService,
    OEWorkerListenerService,
    // root
    OEActionsService,
    OEApiService,
    OECoordinatorService,
    OELoaderService,
    OEStrategyLayoutService,
    OESyncService,
    OEValidatorService,
    OEAllocationBulkService,
    OEAPIChannelService,
    OEUpdateMonitorService,
    //
    ObjectUtils,
    //
  ],
  exports: [OEAlgoParamsComponent, OEScreenContainerComponent],
  bootstrap: [OEScreenContainerComponent, OEUpdateMonitorService],
})
export class OrderEntryModule {}
